import { debug } from "console";
import { ANALYTICS_VIEW_MODE, AnalyticsViewMode, ChartType, PAYER_CATEGORY, PayerCategory } from '../types';

// config/analyticsCharts.jso
//   type: 'bar' | 'line' | 'pie' | 'doughnut' | 'gauge' | 'demographic' | 'data-only' | 'data-value' | 'patient-bar' | 'productivity' | 'multi-chart' | 'donut' | 'bar-chart' | 'horizontal-bar' | 'appointment-stats' | 'distribuited';

export interface ChartConfig {
    id: string;
    source: string;
    order: number;
    title: string;
    type: ChartType;
    cols: number;
    visible: boolean;
    debug?: boolean;
    // Cap on pie/donut slices; the tail is rolled into one "Other" slice. Opt-in, because
    // most pies here have few enough categories to show whole.
    topN?: number;
    // SHER-853. Declared here rather than inferred in the chart, because whether a series
    // is money is a fact about the endpoint, not about the numbers it returns.
    money?: boolean;
    // Translation id naming what chartData.count counts on this chart. Without it the card
    // printed a bare "Total: 403" over a chart of francs.
    totalUnitId?: string;
    // Month-bucketed charts always end on the month that is still running.
    markIncompletePeriod?: boolean;
    // SHER-899. Which payer categories a table reports on. Two rows share one component and one
    // request and differ only in this, so it is a row-level value rather than two components.
    // Closed union: both tables are legitimately empty on most tenants, so a misspelled category
    // would look exactly like the expected state.
    payerCategories?: PayerCategory[];
  }

  // SHER-562. Which view mode of the Outpatients tab a chart belongs to. It is not a display
  // flag: useOutpatientsData fetches only the endpoints the active tier's visible charts
  // need, so an unclassified row is never loaded — it is rendered by neither mode and
  // fetched by neither, with no error to notice.
  //
  // It is required here, and declared on a row type only Outpatients uses, rather than
  // optional on the shared one. Optional-on-shared was how it was first written, and it made
  // that silent failure reachable: the six other tabs have no tiers, so the shared type could
  // not demand it, and nothing rejected an outpatients row that forgot it.
  export interface OutpatientChartConfig extends ChartConfig {
    tier: AnalyticsViewMode;
  }

  export interface SurgeryChartConfig extends ChartConfig {
    tier: AnalyticsViewMode;
  }
  
  interface AnalyticsChartsConfig {
    outpatients: OutpatientChartConfig[];
    surgery: SurgeryChartConfig[];
    inpatients: ChartConfig[];
    laboratory: ChartConfig[];
    radiology: ChartConfig[];
    finances: ChartConfig[];
    staff: ChartConfig[];
    quality: ChartConfig[];
  }

export const analyticsCharts: AnalyticsChartsConfig = {
	outpatients: [
    { id: 'totalPatients', source: 'totalPatients', order: 1, title: 'hospital.analytics.charts.outpatients.totalPatients', type: 'distribuited', cols: 6, visible: true, debug: false, tier: ANALYTICS_VIEW_MODE.QUICK },
    { id: 'totalAppointments', source: 'appointmentDistribution', order: 2, title: 'hospital.analytics.charts.outpatients.totalAppointments', type: 'line', cols: 6, visible: true, debug: false, tier: ANALYTICS_VIEW_MODE.QUICK },
    { id: 'patientsDetails', source: 'patientsDetails', order: 3, title: 'hospital.analytics.charts.outpatients.patientsDetails', type: 'demographic', cols: 12, visible: true, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'sexDistribution', source: 'sexDistribution', order: 4, title: 'hospital.analytics.charts.outpatients.sexDistribution', type: 'sexDistribution', cols: 6, visible: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'newPatients', source: 'newPatients', order: 5, title: 'hospital.analytics.charts.outpatients.newPatients', type: 'distribuited', cols: 6, visible: false, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'appointmentsByType', source: 'appointmentsByType', order: 6, title: 'hospital.analytics.charts.outpatients.appointmentsByType', type: 'distribuited', cols: 6, visible: false, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'averageWaitingTimes', source: 'averageWaitingTimes', order: 7, title: 'hospital.analytics.charts.outpatients.averageWaitingTimes', type: 'bar', cols: 6, visible: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    // SHER-562, review feedback 2026-09-23: this is what the quick view is asked to answer —
    // what came through the door and of which kind — so it moved up from the detail, swapping
    // places with No Show Rate below. It renders as a chart and a table side by side, so it
    // takes the full width the quick view has left under the two counters.
    { id: 'monthlyAppointments', source: 'appointmentTypeData', order: 8, title: 'hospital.analytics.charts.outpatients.monthlyAppointments', type: 'appointment-type-table', cols: 12, visible: true, debug: false, tier: ANALYTICS_VIEW_MODE.QUICK },
    { id: 'pharmacyUsage', source: 'pharmacyUsage', order: 9, title: 'hospital.analytics.charts.outpatients.pharmacyUsage', type: 'bar', cols: 6, visible: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'appointmentsByInsurance', source: 'appointmentsByInsurance', order: 13, title: 'hospital.analytics.charts.outpatients.appointmentsByInsurance', type: 'pie', cols: 6, visible: true, debug: false, topN: 5, tier: ANALYTICS_VIEW_MODE.DETAILED },
    // SHER-899. Both rows read ONE source, which reads the same request as the pie above — the
    // hook resolves a tier to a SET of requests, so this costs one call. They differ only in
    // `payerCategories`, so a third table is one more row here and nothing else. Tables, not
    // slices: the pie caps at five and rolls the tail into "Other", and the insurer NAME is what
    // these exist to show.
    { id: 'topIpm', source: 'insurers', order: 13.1, title: 'hospital.analytics.charts.outpatients.topIpm', type: 'insurance-top-table', cols: 6, visible: true, debug: false, payerCategories: [PAYER_CATEGORY.IPM], topN: 5, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'topInsurers', source: 'insurers', order: 13.2, title: 'hospital.analytics.charts.outpatients.topInsurers', type: 'insurance-top-table', cols: 6, visible: true, debug: false, payerCategories: [PAYER_CATEGORY.INSURANCE, PAYER_CATEGORY.MUTUAL], topN: 5, tier: ANALYTICS_VIEW_MODE.DETAILED },
    // Still rendered, one tier down. A no-show RATE is a fraction, and it misleads in a quick
    // glance until you already know the volume it is a fraction of — which is the detail view's
    // job, not the quick one's.
    { id: 'noShowRate', source: 'appointmentOutcomes', order: 10, title: 'hospital.analytics.charts.outpatients.noShowRate', type: 'appointment-outcomes', cols: 12, visible: true, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'appointmentsByDepartment', source: 'appointementStats', order: 11, title: 'hospital.analytics.charts.outpatients.appointmentsByDepartment', type: 'data-only', cols: 12, visible: false, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'waitTime', source: 'waitTime', order: 12, title: 'hospital.analytics.charts.outpatients.waitTime', type: 'wait-time', cols: 12, visible: true, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'productivity', source: 'appointmentsByDoctor', order: 14, title: 'hospital.analytics.charts.outpatients.productivity', type: 'productivity', cols: 12, visible: true, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'diagnosis', source: 'diagnosis', order: 15, title: 'hospital.analytics.charts.outpatients.diagnosis', type: 'diagnosis-table', cols: 12, visible: true, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'debug', source: 'activityDistribution', order: 16, title: 'hospital.analytics.charts.outpatients.debug', type: 'patient-bar', cols: 12, visible: false, debug: false, tier: ANALYTICS_VIEW_MODE.DETAILED },
	],
  surgery: [
    { id: 'surgeryKpis', source: 'quickKpis', order: 0, title: 'hospital.analytics.charts.surgery.kpis', type: 'surgery-kpi-section', cols: 12, visible: true, tier: ANALYTICS_VIEW_MODE.QUICK },
    { id: 'periodActivity', source: 'periodActivity', order: 1, title: 'hospital.analytics.charts.surgery.periodActivity', type: 'bar-stacked', cols: 6, visible: true, tier: ANALYTICS_VIEW_MODE.QUICK },
    { id: 'topProcedures', source: 'topProcedures', order: 2, title: 'hospital.analytics.charts.surgery.topProcedures', type: 'horizontal-bar', cols: 6, visible: true, topN: 10, tier: ANALYTICS_VIEW_MODE.QUICK },
    { id: 'byProcedure', source: 'byProcedure', order: 3, title: 'hospital.analytics.charts.surgery.byProcedure', type: 'surgery-procedure-table', cols: 12, visible: true, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'bySurgeon', source: 'bySurgeon', order: 4, title: 'hospital.analytics.charts.surgery.bySurgeon', type: 'surgery-surgeon-table', cols: 12, visible: true, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'waitingTime', source: 'waitingTime', order: 5, title: 'hospital.analytics.charts.surgery.waitingTime', type: 'surgery-wait-distribution', cols: 6, visible: true, tier: ANALYTICS_VIEW_MODE.DETAILED },
    { id: 'pipelineByStatus', source: 'pipelineByStatus', order: 6, title: 'hospital.analytics.charts.surgery.pipeline', type: 'surgery-pipeline-status', cols: 6, visible: true, tier: ANALYTICS_VIEW_MODE.DETAILED },
  ],
	inpatients: [
    { id: 'readmissionRate', source: 'readmissionRate', order: 7, title: 'hospital.analytics.charts.inpatients.readmissionRate', type: 'horizontal-bar', cols: 6, visible: false },
    { id: 'pharmacyUsage', source: 'pharmacyUsage', order: 9, title: 'hospital.analytics.charts.inpatients.pharmacyUsage', type: 'line', cols: 6, visible: false },
    { id: 'admissionsByDepartment', source: 'admissionsByDepartment', order: 10, title: 'hospital.analytics.charts.inpatients.admissionsByDepartment', type: 'bar', cols: 12, visible: false },
    { id: 'indicatorsByDepartment', source: 'indicatorsByDepartment', order: 11, title: 'hospital.analytics.charts.inpatients.indicatorsByDepartment', type: 'bar', cols: 6, visible: false, debug: false },
    // SHER-583. Full width and last: it is a table, and it reads as the conclusion of the tab.
    // Not tiered — the Outpatients view-mode toggle is outpatient-only by design (SHER-562), so
    // the Inpatients tab has one view and a `tier` here would be inert.
    { id: 'dischargeDiagnoses', source: 'dischargeDiagnoses', order: 13, title: 'hospital.analytics.charts.inpatients.dischargeDiagnoses', type: 'discharge-diagnosis-table', cols: 12, visible: true, debug: false },
	],
	laboratory: [
    { id: 'testsByDepartment', source: 'totalTests', order: 2, title: 'hospital.analytics.charts.laboratory.testsByDepartment', type: 'pie', cols: 6, visible: true },
    { id: 'testsByType', source: 'testsByType', order: 3, title: 'hospital.analytics.charts.laboratory.testsByType', type: 'doughnut', cols: 6, visible: false },
    { id: 'averageTurnaroundTime', source: 'averageTurnaroundTime', order: 4, title: 'hospital.analytics.charts.laboratory.averageTurnaroundTime', type: 'bar-chart', cols: 6, visible: true, debug: false },
    { id: 'monthlyTests', source: 'monthlyTests', order: 5, title: 'hospital.analytics.charts.laboratory.monthlyTests', type: 'distribuited', cols: 6, visible: true },
    { id: 'patients', source: 'patients', order: 6, title: 'hospital.analytics.charts.laboratory.patients', type: 'bar-chart', cols: 6, visible: true },
    { id: 'location', source: 'location', order: 7, title: 'hospital.analytics.charts.laboratory.location', type: 'bar-chart', cols: 6, visible: true },
    { id: 'insurance', source: 'insurance', order: 8, title: 'hospital.analytics.charts.laboratory.insurance', type: 'pie', cols: 6, visible: false },
    { id: 'type', source: 'type', order: 9, title: 'hospital.analytics.charts.laboratory.type', type: 'bar-chart', cols: 6, visible: true }
],
radiology: [
    { id: 'totalScans', source: 'totalScans', order: 1, title: 'hospital.analytics.charts.radiology.totalScans', type: 'bar-chart', cols: 6, visible: true, debug: false },
    { id: 'turnaroundTime', source: 'turnaroundTime', order: 2, title: 'hospital.analytics.charts.radiology.turnaroundTime', type: 'bar-chart', cols: 6, visible: true },
    { id: 'patientCount', source: 'patientCount', order: 3, title: 'hospital.analytics.charts.radiology.patientCount', type: 'distribuited', cols: 6, visible: true },
    { id: 'monthlyTrends', source: 'monthlyTrends', order: 4, title: 'hospital.analytics.charts.radiology.monthlyTrends', type: 'line', cols: 12, visible: true },
    { id: 'location', source: 'location', order: 5, title: 'hospital.analytics.charts.radiology.location', type: 'pie', cols: 6, visible: true }
],
finances: [
    { id: 'totalBilling', source: 'totalBilling', order: 1, title: 'hospital.analytics.charts.finances.totalBilling', type: 'line', cols: 6, visible: true, money: true, totalUnitId: 'hospital.analytics.finance.unit.invoices', markIncompletePeriod: true },
    { id: 'totalDiscount', source: 'totalDiscount', order: 2, title: 'hospital.analytics.charts.finances.totalDiscount', type: 'bar-chart', cols: 6, visible: true, money: true, totalUnitId: 'hospital.analytics.finance.unit.discounted_invoices', markIncompletePeriod: true },
    { id: 'totalIncome', source: 'totalIncome', order: 3, title: 'hospital.analytics.charts.finances.totalIncome', type: 'bar-chart', cols: 6, visible: true, debug: false, money: true, totalUnitId: 'hospital.analytics.finance.unit.invoices', markIncompletePeriod: true },
    { id: 'billingByLocation', source: 'billingByLocation', order: 4, title: 'hospital.analytics.charts.finances.billingByLocation', type: 'pie', cols: 6, visible: false, money: true },
    { id: 'billingByInsurance', source: 'billingByInsurance', order: 5, title: 'hospital.analytics.charts.finances.billingByInsurance', type: 'pie', cols: 6, visible: false, money: true },
    { id: 'billingByDepartment', source: 'billingByDepartment', order: 6, title: 'hospital.analytics.charts.finances.billingByDepartment', type: 'line', cols: 6, visible: true, money: true, markIncompletePeriod: true },
    { id: 'monthlyBillingTrends', source: 'monthlyBillingTrends', order: 7, title: 'hospital.analytics.charts.finances.monthlyBillingTrends', type: 'line', cols: 12, visible: false, money: true, markIncompletePeriod: true },
    { id: 'outpatientBillingTrends', source: 'outpatientBillingTrends', order: 8, title: 'hospital.analytics.charts.finances.outpatientBillingTrends', type: 'line', cols: 6, visible: true, money: true, markIncompletePeriod: true },
    // { id: 'inpatientBillingTrends', source: 'inpatientBillingTrends', order: 9, title: 'hospital.analytics.charts.finances.inpatientBillingTrends', type: 'line', cols: 6, visible: true },
    { id: 'categoryBillingTrends', source: 'categoryBillingTrends', order: 10, title: 'hospital.analytics.charts.finances.categoryBillingTrends', type: 'category-billing-table', cols: 12, visible: true },
    { id: 'patientCategoryBilling', source: 'patientCategoryBilling', order: 11, title: 'hospital.analytics.charts.finances.patientCategoryBilling', type: 'patient-category-billing', cols: 12, visible: true },
],

staff: [
	{ id: 'totalStaff', source: 'totalStaff', order: 1, title: 'hospital.analytics.charts.staff.totalStaff', type: 'bar', cols: 6, visible: true },
	{ id: 'medicalStaff', source: 'medicalStaff', order: 2, title: 'hospital.analytics.charts.staff.medicalStaff', type: 'bar', cols: 6, visible: true },
	{ id: 'staffByDepartment', source: 'staffByDepartment', order: 3, title: 'hospital.analytics.charts.staff.staffByDepartment', type: 'pie', cols: 6, visible: true },
	{ id: 'staffByCategory', source: 'staffByCategory', order: 4, title: 'hospital.analytics.charts.staff.staffByCategory', type: 'doughnut', cols: 6, visible: true },
	{ id: 'staffingLevels', source: 'staffingLevels', order: 5, title: 'hospital.analytics.charts.staff.staffingLevels', type: 'line', cols: 12, visible: true },
	{ id: 'staffingMix', source: 'staffingMix', order: 6, title: 'hospital.analytics.charts.staff.staffingMix', type: 'bar', cols: 6, visible: true }
],

quality: [
	{ id: 'outpatientsQuality', source: 'outpatientsQuality', order: 1, title: 'hospital.analytics.charts.quality.outpatientsQuality', type: 'bar', cols: 6, visible: true },
	{ id: 'inpatientsQuality', source: 'inpatientsQuality', order: 2, title: 'hospital.analytics.charts.quality.inpatientsQuality', type: 'bar', cols: 6, visible: true },
	{ id: 'staffQuality', source: 'staffQuality', order: 3, title: 'hospital.analytics.charts.quality.staffQuality', type: 'bar', cols: 6, visible: true },
	{ id: 'requestsQuality', source: 'requestsQuality', order: 4, title: 'hospital.analytics.charts.quality.requestsQuality', type: 'line', cols: 12, visible: true }
],
 };

 /**
  * SHER-562. The one tier predicate, shared by Outpatients and Surgery. The hook asks it which
  * endpoints to fetch and the tab asks it what to render, so the two cannot drift — an earlier
  * version wrote the predicate twice and they already disagreed about debug charts, which the
  * tab rendered but the hook never fetched.
  *
  * **The tiers are NESTED, not disjoint.** Analyse détaillée is the complete view of a tab;
  * Vue rapide is a reduced default showing only the rows marked quick. What separates the two
  * is what gets LOADED when the page opens, not what exists — quick rows appear in both modes,
  * and switching up a tier adds the rest rather than replacing them.
  *
  * Round 1 read `tier` as a partition (`chart.tier === mode`), so detailed showed only the
  * charts absent from quick. The client reported that on 2026-09-18, and his 2026-08-30
  * comment on the ticket had already specified the nested reading. The unified `order`
  * sequence on the rows above is the other tell: a config meant for two disjoint views would
  * never have needed one.
  *
  * Surgery follows the same rule, and briefly did not. Its rows arrived with SHER-902 as a
  * copy of the outpatient pattern — that spec says "extend the SHER-562 view-mode toggle to
  * this tab" and "tier test like outpatients" — so the exclusivity was inherited from round
  * 1's misreading, never chosen for Surgery on its own merits. Leaving it behind would have
  * been user-visible rather than merely untidy: one `viewMode` drives both tabs from one
  * toggle and survives a tab change, so a user on Analyse détaillée could watch three Surgery
  * charts disappear just by clicking across from Outpatients.
  *
  * `chart.tier !== undefined` is not redundant with `mode === DETAILED`. A row with no tier
  * belongs to no mode — neither rendered nor fetched, in either — and that invariant is
  * load-bearing: `tier` is required by the row types, but nothing in CI type-checks
  * (babel-jest strips types), so runtime behaviour is the contract. A bare
  * `mode === DETAILED ||` would let an untiered row appear in detailed, quietly moving a rule
  * nobody asked to move.
  */
 const chartsForMode = <T extends ChartConfig & { tier?: AnalyticsViewMode }>(
   rows: T[],
   mode: AnalyticsViewMode,
 ): T[] =>
   rows
     .filter(
       (chart) =>
         (chart.visible || chart.debug) &&
         chart.tier !== undefined &&
         (mode === ANALYTICS_VIEW_MODE.DETAILED || chart.tier === mode),
     )
     .sort((a, b) => a.order - b.order);

 export const outpatientChartsForMode = (mode: AnalyticsViewMode): OutpatientChartConfig[] =>
   chartsForMode(analyticsCharts.outpatients, mode);

 export const surgeryChartsForMode = (mode: AnalyticsViewMode): SurgeryChartConfig[] =>
   chartsForMode(analyticsCharts.surgery, mode);

 export const chartColors = {
  seriesColors: [
    '#1E88E5', // Blue
    '#8E24AA', // Purple
    '#43A047', // Green
    '#FFB300', // Amber
    '#E53935', // Red
    '#9C27B0', // Deep Purple
    '#795548', // Brown
    '#607D8B', // Blue Grey
  ],
  textColor: '#666666', // Medium Grey for labels
  dividerColor: '#D3D3D3', // Light Grey for gridlines and borders
};

// Define consistent colors for specific categories across charts
export const categoryColorMap: { [key: string]: string } = {
  "No department": chartColors.seriesColors[0], // Blue
  "Service de Chirurgie": chartColors.seriesColors[1], // Purple
  "Service d'Anesthésie": chartColors.seriesColors[4], // Red
  "Service de kinésithérapie": chartColors.seriesColors[2], // Green
  "Service de Médecine": chartColors.seriesColors[3], // Amber
  // Add variants for Salle 1 / Salle 2 if needed, maintaining base color logic
  "No department - Salle 1": chartColors.seriesColors[0], // Blue
  "No department - Salle 2": '#1E88E5', // Slightly different Blue variant if needed, or same blue
  "Service de Chirurgie - Salle 1": chartColors.seriesColors[1], // Purple
  "Service de Chirurgie - Salle 2": '#8E24AA', // Slightly different Purple variant if needed, or same purple
  "Service de kinésithérapie - Salle 1": chartColors.seriesColors[2], // Green
  "Service de kinésithérapie - Salle 2": '#43A047', // Slightly different Green variant if needed, or same green
  "Service de Médecine - Salle 1": chartColors.seriesColors[3], // Amber
  "Service de Médecine - Salle 2": '#FFB300', // Slightly different Amber variant if needed, or same amber
  // Add other potential categories as needed
};

// Base department colors - for more robust matching
const departmentBaseColors: { [key: string]: string } = {
  "anesthesie": chartColors.seriesColors[4], // Red
  "anesthésie": chartColors.seriesColors[4],
  "chirurgie": chartColors.seriesColors[1], // Purple
  "kinesitherapie": chartColors.seriesColors[2], // Green
  "kinésithérapie": chartColors.seriesColors[2],
  "medecine": chartColors.seriesColors[3], // Amber
  "médecine": chartColors.seriesColors[3],
};

/**
 * Gets a color for a category with smart matching to handle variations
 * @param categoryName The category or department name
 * @returns The color to use for this category
 */
export const getCategoryColor = (categoryName: string): string => {
  // 1. First try exact match from the map
  if (categoryColorMap[categoryName]) {
    return categoryColorMap[categoryName];
  }
  
  // 2. Try case-insensitive match
  const lowerCaseName = categoryName.toLowerCase();
  const keys = Object.keys(categoryColorMap);
  const caseInsensitiveMatch = keys.find(key => key.toLowerCase() === lowerCaseName);
  if (caseInsensitiveMatch) {
    return categoryColorMap[caseInsensitiveMatch];
  }
  
  // 3. Look for base department name within the string
  for (const [deptKey, color] of Object.entries(departmentBaseColors)) {
    if (lowerCaseName.includes(deptKey)) {
      return color;
    }
  }
  
  // 4. Fallback to default color based on first character
  const firstChar = (categoryName.charAt(0) || 'A').toUpperCase();
  const charCode = firstChar.charCodeAt(0);
  const colorIndex = charCode % chartColors.seriesColors.length;
  return chartColors.seriesColors[colorIndex];
};

// Optional: Add a function to retrieve colors if you want flexibility later
export const getChartColors = () => chartColors;